import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Copy, KeyRound } from "lucide-react";
import { useDateFormat } from "../../hooks/useDateFormat";
import { api } from "@jourfx/convex/client";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  useToast,
} from "@jourfx/ui";

type Id<T extends string> = string & { __tableName: T };

type ProfileDetail = {
  _id: string;
  organizationId: string;
  organizationName?: string | null;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  versichertennummer?: string;
  begutachtungsGrund?: string;
  registrationSource?: "internal" | "self_service";
  notes?: string;
  accessCode?: string;
  assignedGutachterId?: string;
  assignedTemplateId?: string;
  invitations: Array<{
    _id: string;
    status: string;
    createdAt: number;
    gutachterName?: string;
    templateName?: string;
    appointmentId?: string;
  }>;
  callbackEntries: Array<{
    _id: string;
    createdAt: number;
    notes?: string;
  }>;
};

export function VersicherteDetail() {
  const { versicherteId } = useParams({ strict: false }) as { versicherteId: string };
  const { t } = useTranslation("versicherte");
  const { toast } = useToast();
  const { formatDateTime } = useDateFormat();
  const apiRef = api as any;

  const profile = useQuery(apiRef.admin.versicherte.get, {
    versichertenProfileId: versicherteId as Id<"versichertenProfiles">,
  }) as ProfileDetail | null | undefined;

  const gutachterList = useQuery(
    apiRef.admin.appointments.listGutachter,
    profile?.organizationId
      ? { organizationId: profile.organizationId as any }
      : "skip"
  ) as Array<{ _id: string; name: string }> | undefined;

  const templates = useQuery(
    apiRef.admin.appointmentTemplates.list,
    profile?.organizationId
      ? { organizationId: profile.organizationId as any }
      : "skip"
  ) as Array<{ _id: string; name: string; duration: number }> | undefined;

  const updateProfile = useMutation(apiRef.admin.versicherte.update);
  const generateCredentials = useMutation(apiRef.admin.versicherte.generateCredentials);

  const [selectedGutachterId, setSelectedGutachterId] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingCredentials, setIsGeneratingCredentials] = useState(false);
  const [generatedAccessCode, setGeneratedAccessCode] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");

  const currentInvitation = useMemo(
    () => profile?.invitations?.[0] ?? null,
    [profile?.invitations]
  );

  useEffect(() => {
    if (!profile) return;

    setFirstName(profile.firstName || "");
    setLastName(profile.lastName || "");
    setEmail(profile.email || "");
    setPhone(profile.phone || "");
    setNotes(profile.notes || "");
    setSelectedGutachterId(profile.assignedGutachterId || "");
    setSelectedTemplateId(profile.assignedTemplateId || "");
  }, [profile]);

  if (profile === undefined) {
    return <p className="text-muted-foreground">{t("detail.loading")}</p>;
  }

  if (!profile) {
    return (
      <div className="space-y-4">
        <Link
          to="/versicherte"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("detail.back")}
        </Link>
        <p>{t("detail.notFound")}</p>
      </div>
    );
  }

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await updateProfile({
        versichertenProfileId: versicherteId as Id<"versichertenProfiles">,
        patch: {
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          notes: notes.trim() || undefined,
          assignedGutachterId: selectedGutachterId || undefined,
          assignedTemplateId: selectedTemplateId || undefined,
        },
      });
      toast({ title: t("detail.toasts.savedTitle") });
    } catch (error) {
      toast({
        title: t("detail.toasts.errorTitle"),
        description: error instanceof Error ? error.message : t("detail.toasts.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateCredentials = async () => {
    setIsGeneratingCredentials(true);
    try {
      const result = await generateCredentials({
        versichertenProfileId: versicherteId as Id<"versichertenProfiles">,
      });
      setGeneratedAccessCode(result.accessCode);
      toast({ title: t("detail.toasts.credentialsGeneratedTitle") });
    } catch (error) {
      toast({
        title: t("detail.toasts.errorTitle"),
        description: error instanceof Error ? error.message : t("detail.toasts.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsGeneratingCredentials(false);
    }
  };

  const appointmentSearch =
    profile.organizationId
      ? ({ organizationId: profile.organizationId, versicherteId: profile._id } as any)
      : ({ versicherteId: profile._id } as any);

  return (
    <div className="space-y-6">
      <Link
        to="/versicherte"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("detail.back")}
      </Link>

      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {profile.firstName} {profile.lastName}
          </h1>
          <p className="text-sm text-muted-foreground">
            {profile.organizationName || t("title")}
            {profile.dateOfBirth ? ` · ${profile.dateOfBirth}` : ""}
          </p>
        </div>
        {currentInvitation ? (
          <Badge variant={currentInvitation.status === "open" ? "outline" : "secondary"}>
            {t("detail.latestInvitation", { status: currentInvitation.status })}
          </Badge>
        ) : null}
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">{t("credentials.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between rounded-md border p-3">
              <span className="text-sm text-muted-foreground">
                {t("credentials.accessCode")}
              </span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold">
                {generatedAccessCode || profile.accessCode || "-"}
                </span>
                {generatedAccessCode || profile.accessCode ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      navigator.clipboard.writeText(generatedAccessCode || profile.accessCode || "")
                    }
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </div>
            </div>

            {!profile.accessCode ? (
              <Button
                variant="outline"
                onClick={handleGenerateCredentials}
                disabled={isGeneratingCredentials}
              >
                <KeyRound className="mr-2 h-4 w-4" />
                {t("credentials.generate")}
              </Button>
            ) : null}

            <p className="text-xs text-muted-foreground">{t("credentials.hint")}</p>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base">{t("detail.profileTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="profile-first-name">{t("detail.fields.firstName")}</Label>
              <Input
                id="profile-first-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-last-name">{t("detail.fields.lastName")}</Label>
              <Input
                id="profile-last-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-email">{t("detail.fields.email")}</Label>
              <Input
                id="profile-email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-phone">{t("detail.fields.phone")}</Label>
              <Input
                id="profile-phone"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-gutachter">{t("detail.fields.assignedGutachter")}</Label>
              <select
                id="profile-gutachter"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedGutachterId}
                onChange={(event) => setSelectedGutachterId(event.target.value)}
              >
                <option value="">{t("detail.noneAssigned")}</option>
                {gutachterList?.map((entry) => (
                  <option key={entry._id} value={entry._id}>
                    {entry.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-template">{t("detail.fields.assignedTemplate")}</Label>
              <select
                id="profile-template"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
              >
                <option value="">{t("detail.noneAssigned")}</option>
                {templates?.map((entry) => (
                  <option key={entry._id} value={entry._id}>
                    {entry.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="profile-notes">{t("detail.fields.notes")}</Label>
              <textarea
                id="profile-notes"
                className="min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </div>
            <div className="md:col-span-2 flex flex-wrap justify-between gap-3">
              <Link to="/appointments/new" search={appointmentSearch}>
                <Button variant="outline">{t("detail.createAppointment")}</Button>
              </Link>
              <Button onClick={handleSaveProfile} disabled={isSaving}>
                {t("detail.save")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("detail.invitationsTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {profile.invitations.length ? (
              profile.invitations.map((invitation) => (
                <div key={invitation._id} className="rounded-md border p-3">
                  <div className="font-medium">{invitation.templateName || t("detail.noTemplate")}</div>
                  <div className="text-sm text-muted-foreground">
                    {invitation.gutachterName || t("detail.noExaminer")} · {invitation.status}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">{t("detail.noInvitations")}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("detail.callbackTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {profile.callbackEntries.length ? (
              profile.callbackEntries.map((entry) => (
                <div key={entry._id} className="rounded-md border p-3">
                  <div className="text-sm text-muted-foreground">{formatDateTime(entry.createdAt)}</div>
                  <div className="mt-1 text-sm">{entry.notes || t("detail.noNotes")}</div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">{t("detail.noCallbacks")}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
